/**
 * Fifty Rooms — product page behaviour (docs/spec/01_product_page.md, zones 4, 5, 6, 7, 9 + sticky bar).
 * Loaded only on the product template (snippets/product-information-fr-assets.liquid), as an ES module.
 *
 *  1. Design swatches / type cards (`a[data-fr-nav-link]`): real links, upgraded to Section Rendering API fetch
 *     (`?sections=<main>,<refresh…>`) + DOM swap + history.pushState / popstate. Any failure -> normal navigation.
 *  2. <fr-complete-the-set>: add-on rows; publishes `fr:set-change` {items, count, total, qty, currency}.
 *  3. Main Add to cart: label "Add to cart — $169" / "Add N items — $total" (desktop + mobile; when it does not fit one
 *     line: "Total: $X" line above the button row + "Add N items", `data-fr-atc-compact`); with ≥ 1 add-on the submit is taken
 *     over: ONE POST /cart/add.js {items:[main, …add-ons with properties._set], sections}. 422 -> retry item by item,
 *     clear message, nothing lost. Cart drawer is refreshed through Horizon's standard CartLinesUpdateEvent.
 *  4. Express checkout hidden while ≥ 1 add-on row is checked (it would buy the main item only).
 *  6. Size pills (`a[data-fr-size]`, blocks/fr-size-picker.liquid): real `?variant=` links upgraded in place - form
 *     `id`, heading price, button label, sticky bar, Complete the set main variant, URL (replaceState). The chosen size
 *     is kept across a design switch when the next design has it. Add-on rows with sizes: `select[data-fr-set-size]`.
 *  5. <fr-sticky-bar>: mobile bar, IntersectionObserver (main button above viewport, footer not visible); title,
 *     variant, item count and live total incl. ticked Complete the set rows. Money: shop money format (strings JSON).
 *
 * No dependencies besides `@shopify/events` (Horizon importmap), imported lazily so a missing CDN never breaks
 * the page.
 */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const MAIN_SELECTOR = '[data-fr-pdp-main]';

/* ------------------------------------------------------------------ helpers */

/** @type {Promise<any> | null} */
let eventsModule = null;
function loadEvents() {
  if (!eventsModule) eventsModule = import('@shopify/events').catch(() => ({}));
  return eventsModule;
}

const MONEY_PLACEHOLDERS = {
  amount: [',', '.', 2],
  amount_no_decimals: [',', '.', 0],
  amount_with_comma_separator: ['.', ',', 2],
  amount_no_decimals_with_comma_separator: ['.', ',', 0],
  amount_with_apostrophe_separator: ["'", '.', 2],
  amount_no_decimals_with_space_separator: [' ', '.', 0],
  amount_with_space_separator: [' ', ',', 2],
  amount_with_period_and_space_separator: [' ', '.', 2],
};

/** Minor units of a currency (JPY 0, USD 2…). @param {string} currency */
function currencyDigits(currency) {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/**
 * Formats minor units like Liquid `money_without_trailing_zeros`: with the shop money format (`shop.money_format`,
 * e.g. "${{amount}}") when it belongs to this currency, otherwise Intl with the currency code. No hard-coded symbol.
 * @param {number} cents @param {string} [currency] @param {string} [format] @param {string} [formatCurrency]
 */
export function formatMoney(cents, currency, format, formatCurrency) {
  const value = Number(cents) || 0;
  const code = currency || formatCurrency || 'USD';
  const digits = currencyDigits(code);
  const divisor = 10 ** digits;
  const whole = value % divisor === 0;
  if (format && (!formatCurrency || formatCurrency === code) && /{{\s*\w+\s*}}/.test(format)) {
    return format.replace(/{{\s*(\w+)\s*}}/g, (_, key) => {
      if (key === 'currency') return code;
      const [thousands, decimal, precision] = MONEY_PLACEHOLDERS[key] || MONEY_PLACEHOLDERS.amount;
      const p = whole ? 0 : Math.min(precision, digits);
      const [a, b] = (value / divisor).toFixed(p).split('.');
      const grouped = a.replace(/\d(?=(\d{3})+(?!\d))/g, `$&${thousands}`);
      return b ? `${grouped}${decimal}${b}` : grouped;
    });
  }
  const fraction = whole ? 0 : digits;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
    }).format(value / divisor);
  } catch {
    return `${(value / divisor).toFixed(fraction)} ${code}`;
  }
}

/** @param {Element | null} el */
function sectionOf(el) {
  return /** @type {HTMLElement | null} */ (el?.closest('.shopify-section') || null);
}

/** @param {Element | null} section */
function stringsFor(section) {
  const el = section?.querySelector('script[data-fr-pdp-strings]') || document.querySelector('script[data-fr-pdp-strings]');
  try {
    return JSON.parse(el?.textContent || '{}');
  } catch {
    return {};
  }
}

/** @param {string} template @param {Record<string, string | number>} values */
function fill(template, values) {
  return Object.entries(values).reduce((s, [k, v]) => s.split(`[${k}]`).join(String(v)), template || '');
}

let liveRegion = /** @type {HTMLElement | null} */ (null);
/** @param {string} text */
function announce(text) {
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('role', 'status');
    liveRegion.dataset.frPdpLive = '';
    document.body.append(liveRegion);
  }
  liveRegion.textContent = '';
  requestAnimationFrame(() => {
    if (liveRegion) liveRegion.textContent = text;
  });
}

/* ------------------------------------------------------- product form access */

/** @param {Element} section */
function productFormOf(section) {
  return /** @type {HTMLElement | null} */ (section.querySelector('product-form-component'));
}

/** @param {Element} section */
function formOf(section) {
  return /** @type {HTMLFormElement | null} */ (productFormOf(section)?.querySelector('form') || null);
}

/** @param {Element} section */
function addButtonOf(section) {
  return /** @type {HTMLButtonElement | null} */ (
    productFormOf(section)?.querySelector('add-to-cart-component button[name="add"], button[type="submit"][name="add"]') ||
      null
  );
}

/** @param {Element} section */
function quantityOf(section) {
  const input = /** @type {HTMLInputElement | null} */ (formOf(section)?.querySelector('input[name="quantity"]') || null);
  return Math.max(1, Number(input?.value) || 1);
}

/** @param {Element} section */
function setOf(section) {
  return /** @type {FrCompleteTheSet | null} */ (section.querySelector('fr-complete-the-set'));
}

/**
 * Current purchase state of a product section.
 * @param {Element} section
 */
export function stateOf(section) {
  const priceEl = /** @type {HTMLElement | null} */ (section.querySelector('[data-fr-main-price]'));
  const set = setOf(section);
  const mainPrice = Number(priceEl?.dataset.frMainPrice ?? set?.dataset.mainPrice) || 0;
  const currency = priceEl?.dataset.frCurrency || set?.dataset.currency || window.Shopify?.currency?.active || 'USD';
  const qty = quantityOf(section);
  const items = set ? set.selectedItems() : [];
  const addonsTotal = items.reduce((sum, item) => sum + item.price, 0);
  const count = qty + items.length;
  const total = mainPrice * qty + addonsTotal;
  // Quantity discount (theme settings → strings.bundle_tiers, empty while the Shopify discount is off): the best
  // tier whose quantity is reached; percentage per cart line, cents rounded — the way the cart allocates it.
  // Items already in the cart are NOT counted here (the cart is the source of truth for the final total).
  // Only items the discount applies to count (strings.bundle_main / data-bundle="false" on a row: product types
  // listed in settings.fr_bundle_exclude get no discount).
  const strings = stringsFor(section);
  const tiers = bundleTiers(strings);
  const mainBundle = strings.bundle_main !== false;
  const main = { qty, price: mainPrice, bundle: mainBundle };
  const { count: bundleCount, percent, saving } = bundleSaving(tiers, main, items);
  return {
    qty,
    mainPrice,
    currency,
    items: items.map(({ id, price, title, type }) => ({ id, price, title, type })),
    count,
    bundleCount,
    total,
    percent,
    saving,
    payable: total - saving,
    tiers,
    main,
    rows: set ? set.rowItems() : [],
    selected: items,
  };
}

/**
 * Quantity discount of a selection: count of eligible items, best reached tier, saving in minor units.
 * Percentage per cart line, cents rounded (the main product with its quantity is ONE line; every add-on is its own
 * line with properties._set) — the way the cart allocates the discount.
 * @param {number[][]} tiers @param {{qty: number, price: number, bundle: boolean}} main
 * @param {{price: number, bundle: boolean}[]} items
 */
export function bundleSaving(tiers, main, items) {
  const bundleItems = items.filter((item) => item.bundle);
  const count = (main.bundle ? main.qty : 0) + bundleItems.length;
  const percent = tiers.reduce((best, [q, p]) => (count >= q && p > best ? p : best), 0);
  const lines = [main.bundle ? main.price * main.qty : 0, ...bundleItems.map((item) => item.price)];
  const saving = percent > 0 ? lines.reduce((sum, line) => sum + Math.round((line * percent) / 100), 0) : 0;
  return { count, percent, saving };
}

/**
 * Valid quantity-discount tiers [[qty, percent], …], lowest quantity first.
 * @param {Record<string, any>} strings
 * @returns {number[][]}
 */
function bundleTiers(strings) {
  const raw = Array.isArray(strings.bundle_tiers) ? strings.bundle_tiers : [];
  return raw
    .map((t) => [Number(t?.[0]), Number(t?.[1])])
    .filter(([q, p]) => q > 1 && p > 0 && p < 100)
    .sort((a, b) => a[0] - b[0]);
}

/**
 * "Bundle savings −$28.70 (10% off, applied in cart)" line above the buy-buttons row (created once).
 * @param {HTMLButtonElement} button
 */
function savingLineOf(button) {
  const row = button.closest('.product-form-buttons') || button.parentElement;
  if (!row || !row.parentElement) return null;
  let line = /** @type {HTMLElement | null} */ (row.parentElement.querySelector(':scope > [data-fr-atc-saving]'));
  if (!line) {
    line = document.createElement('p');
    line.className = 'fr-atc-saving';
    line.dataset.frAtcSaving = '';
    line.setAttribute('aria-live', 'polite');
    line.hidden = true;
    row.before(line);
  }
  return line;
}

/** @param {HTMLButtonElement} button */
function labelHost(button) {
  let host = /** @type {HTMLElement | null} */ (button.querySelector('[data-fr-atc-label]'));
  if (host) return host;
  host = document.createElement('span');
  host.className = 'fr-atc-label';
  host.dataset.frAtcLabel = '';
  const textNode = Array.from(button.childNodes).find((n) => n.nodeType === Node.TEXT_NODE && n.textContent?.trim());
  if (textNode) textNode.replaceWith(host);
  else button.prepend(host);
  return host;
}

/**
 * "Total: $287" line above the buy-buttons row (created once, hidden unless the button label does not fit).
 * @param {HTMLButtonElement} button
 */
function totalLineOf(button) {
  const row = button.closest('.product-form-buttons') || button.parentElement;
  if (!row || !row.parentElement) return null;
  let line = /** @type {HTMLElement | null} */ (row.parentElement.querySelector(':scope > [data-fr-atc-total]'));
  if (!line) {
    line = document.createElement('p');
    line.className = 'fr-atc-total';
    line.dataset.frAtcTotal = '';
    line.setAttribute('aria-live', 'polite');
    line.hidden = true;
    row.before(line);
  }
  return line;
}

/** @type {WeakMap<HTMLButtonElement, number>} */
const fitWidths = new WeakMap();
/** @type {ResizeObserver | null} */
let fitObserver = null;

/**
 * Compact mode (`data-fr-atc-compact` on the button) when "Add 3 items — $287" wraps or overflows.
 * @param {HTMLButtonElement} button
 */
function fitLabel(button) {
  const host = /** @type {HTMLElement | null} */ (button.querySelector('[data-fr-atc-label]'));
  const line = /** @type {HTMLElement | null} */ (
    (button.closest('.product-form-buttons') || button.parentElement)?.parentElement?.querySelector(':scope > [data-fr-atc-total]') || null
  );
  button.removeAttribute('data-fr-atc-compact');
  let compact = false;
  if (host && button.offsetWidth > 0) {
    const cs = getComputedStyle(host);
    const lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4 || 20;
    const wraps = host.getBoundingClientRect().height > lh * 1.5;
    const overflows = button.scrollWidth > button.clientWidth + 1 || host.scrollWidth > host.clientWidth + 1;
    compact = wraps || overflows;
  }
  button.toggleAttribute('data-fr-atc-compact', compact);
  if (line) line.hidden = !compact;
  fitWidths.set(button, button.offsetWidth);
  if (!fitObserver && 'ResizeObserver' in window) {
    fitObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const b = /** @type {HTMLButtonElement} */ (entry.target);
        if (!b.isConnected || b.disabled) continue;
        if (fitWidths.get(b) === b.offsetWidth) continue;
        fitLabel(b);
      }
    });
  }
  fitObserver?.observe(button);
}

/**
 * Re-renders everything that depends on the set state and publishes `fr:set-change`.
 * @param {Element | null} section
 */
export function update(section) {
  if (!section) return;
  const strings = stringsFor(section);
  const state = stateOf(section);
  const totalText = formatMoney(state.payable, state.currency, strings.money_format, strings.money_currency);
  const label =
    state.items.length > 0
      ? fill(strings.add_items || 'Add [count] items', { count: state.count })
      : strings.add_to_cart || 'Add to cart';
  const countText = fill(state.count === 1 ? strings.items_one || '[count] item' : strings.items_other || '[count] items', {
    count: state.count,
  });

  // Zone 6: main button label "Add 3 items — $287" (desktop and mobile); if it does not fit one line, the total
  // moves to a "Total: $287" line above the button row and the button keeps "Add 3 items".
  const button = addButtonOf(section);
  if (button) {
    const line = totalLineOf(button);
    if (!button.disabled) {
      const host = labelHost(button);
      host.textContent = label;
      const total = document.createElement('span');
      total.className = 'fr-atc-label__total';
      total.textContent = ` — ${totalText}`;
      host.append(total);
      if (line) line.textContent = fill(strings.total || 'Total: [total]', { total: totalText });
      fitLabel(button);
    } else if (line) {
      line.hidden = true;
    }

    // Quantity discount: saving for the items that will be added (only with active tiers)
    if (state.tiers.length) {
      const saving = savingLineOf(button);
      if (saving) {
        saving.hidden = button.disabled || state.saving <= 0;
        saving.dataset.amount = String(state.saving);
        saving.dataset.percent = String(state.percent);
        saving.textContent =
          state.saving > 0
            ? fill(strings.bundle_saving || '', {
                amount: formatMoney(state.saving, state.currency, strings.money_format, strings.money_currency),
                percent: state.percent,
              })
            : '';
      }
    }
  }

  // Zone 7: express checkout only buys the main item -> hide while add-ons are checked
  const set = setOf(section);

  // Quantity discount offer under the price: "Buy 2, save $27.04 · Buy 3, save $60.84" for THIS product (price of
  // the chosen size), "8% off 2 items · 12% off 3+ items · applied in cart".
  renderOffer(section, state, strings);

  // Complete the set rows: what each row adds to the saving of the whole selection (main qty + ticked rows),
  // "save $19.04"; hidden for rows without a discount (excluded groups) or when it adds nothing.
  if (set) {
    for (const row of state.rows) {
      const host = row.input.closest('[data-fr-set-row]')?.querySelector('.fr-cts__text');
      if (!host) continue;
      let el = /** @type {HTMLElement | null} */ (host.querySelector(':scope > [data-fr-set-row-saving]'));
      const others = state.selected.filter((item) => item.input !== row.input);
      const delta =
        state.tiers.length && row.bundle
          ? bundleSaving(state.tiers, state.main, [...others, row]).saving - bundleSaving(state.tiers, state.main, others).saving
          : 0;
      if (delta <= 0) {
        if (el) {
          el.hidden = true;
          el.dataset.amount = '0';
        }
        continue;
      }
      if (!el) {
        el = document.createElement('span');
        el.className = 'fr-cts__row-saving';
        el.dataset.frSetRowSaving = '';
        host.append(el);
      }
      const text = fill(strings.bundle_row_saving || 'save [amount]', {
        amount: formatMoney(delta, state.currency, strings.money_format, strings.money_currency),
      });
      if (el.textContent !== text) el.textContent = text;
      el.dataset.amount = String(delta);
      el.hidden = false;
    }
  }

  // Complete the set header: "Add 2 items — save 10%" (promise of the first tier) / the tier reached by the ticks
  const bundle = set?.querySelector('[data-fr-set-bundle]');
  if (bundle && state.tiers.length) {
    const reached = state.percent > 0;
    const hintCount = reached ? state.bundleCount : state.tiers[0][0];
    const hintPercent = reached ? state.percent : state.tiers[0][1];
    const text = fill(strings.bundle_hint || '', { count: hintCount, percent: hintPercent });
    if (bundle.textContent !== text) bundle.textContent = text;
    /** @type {HTMLElement} */ (bundle).dataset.percent = String(hintPercent);
  }
  const hideExpress = state.items.length > 0 && set?.dataset.hideExpress !== 'false';
  productFormOf(section)?.toggleAttribute('data-fr-set-active', hideExpress);

  // Sticky bar
  const bar = /** @type {FrStickyBar | null} */ (section.querySelector('fr-sticky-bar'));
  bar?.render({ label, total: totalText, count: countText });

  (set || section).dispatchEvent(
    new CustomEvent('fr:set-change', {
      bubbles: true,
      detail: {
        items: state.items,
        count: state.count,
        total: state.total,
        qty: state.qty,
        currency: state.currency,
        percent: state.percent,
        saving: state.saving,
        payable: state.payable,
      },
    })
  );
}

/**
 * Quantity discount offer under title + price ([data-fr-bundle-offer], rendered by blocks/fr-pdp-heading.liquid only
 * while the discount is active and this product group gets it). Re-rendered here so a size change updates the amounts.
 * @param {Element} section @param {ReturnType<typeof stateOf>} state @param {Record<string, any>} strings
 */
function renderOffer(section, state, strings) {
  const offer = section.querySelector('[data-fr-bundle-offer]');
  if (!offer) return;
  if (!state.tiers.length || !state.main.bundle || state.mainPrice <= 0 || !strings.bundle_offer_item) {
    /** @type {HTMLElement} */ (offer).hidden = true;
    return;
  }
  const last = state.tiers.length - 1;
  const deal = state.tiers
    .map(([q, p]) =>
      fill(strings.bundle_offer_item, {
        count: q,
        amount: formatMoney(Math.round((state.mainPrice * q * p) / 100), state.currency, strings.money_format, strings.money_currency),
      })
    )
    .join(' · ');
  const note = [
    ...state.tiers.map(([q, p], i) => fill(i === last ? strings.bundle_offer_tier_top : strings.bundle_offer_tier, { count: q, percent: p })),
    strings.bundle_offer_note,
  ]
    .filter(Boolean)
    .join(' · ');
  const dealEl = offer.querySelector('[data-fr-bundle-offer-deal]');
  const noteEl = offer.querySelector('[data-fr-bundle-offer-note]');
  if (dealEl && dealEl.textContent !== deal) dealEl.textContent = deal;
  if (noteEl && noteEl.textContent !== note) noteEl.textContent = note;
  /** @type {HTMLElement} */ (offer).hidden = false;
}

/* ------------------------------------------------------- Complete the set */

class FrCompleteTheSet extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    queueMicrotask(() => update(sectionOf(this)));
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /** @returns {{id: string, price: number, title: string, type: string, input: HTMLInputElement}[]} */
  selectedItems() {
    return Array.from(this.querySelectorAll('input[data-fr-set-check]:checked:not(:disabled)')).map((el) => {
      const input = /** @type {HTMLInputElement} */ (el);
      return {
        id: input.value,
        price: Number(input.dataset.price) || 0,
        title: input.dataset.title || '',
        type: input.dataset.type || 'standard',
        bundle: input.dataset.bundle !== 'false',
        input,
      };
    });
  }

  /** Every enabled row (ticked or not), same shape as selectedItems(). */
  rowItems() {
    return Array.from(this.querySelectorAll('input[data-fr-set-check]:not(:disabled)')).map((el) => {
      const input = /** @type {HTMLInputElement} */ (el);
      return { price: Number(input.dataset.price) || 0, bundle: input.dataset.bundle !== 'false', input };
    });
  }

  /** Unchecks the given variant ids (or all rows). @param {string[]} [ids] */
  reset(ids) {
    for (const el of this.querySelectorAll('input[data-fr-set-check]')) {
      const input = /** @type {HTMLInputElement} */ (el);
      if (!ids || ids.includes(input.value)) input.checked = false;
    }
  }

  /** @param {string} message */
  showError(message) {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[data-fr-set-error]'));
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    if (event.target instanceof HTMLSelectElement && event.target.matches('[data-fr-set-size]')) {
      this.#selectSize(event.target);
      return;
    }
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-fr-set-check]')) return;
    this.showError('');
    update(sectionOf(this));
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const swatch = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-fr-set-swatch]') : null
    );
    if (!swatch) return;
    event.preventDefault();
    this.#selectSwatch(swatch);
  };

  /** Arrow keys inside a picker radiogroup. @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('[data-fr-set-swatch]') : null;
    if (!swatch) return;
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    const step = keys[/** @type {keyof typeof keys} */ (event.key)];
    if (!step) return;
    event.preventDefault();
    const all = Array.from(swatch.parentElement?.querySelectorAll('[data-fr-set-swatch]') || []);
    const next = /** @type {HTMLElement} */ (all[(all.indexOf(swatch) + step + all.length) % all.length]);
    this.#selectSwatch(next);
    next.focus();
  };

  /** Size of an add-on row: the checkbox carries the chosen variant and its price. @param {HTMLSelectElement} select */
  #selectSize(select) {
    const row = select.closest('[data-fr-set-row]');
    const option = select.selectedOptions[0];
    const input = /** @type {HTMLInputElement | null} */ (row?.querySelector('[data-fr-set-check]') || null);
    if (!row || !option || !input) return;
    input.value = option.value;
    input.dataset.price = option.dataset.price || input.dataset.price;
    const sub = row.querySelector('[data-fr-set-sub]');
    if (sub && option.dataset.sub) sub.textContent = option.dataset.sub;
    this.showError('');
    update(sectionOf(this));
  }

  /** @param {HTMLElement} swatch */
  #selectSwatch(swatch) {
    const row = swatch.closest('[data-fr-set-row]');
    if (!row) return;
    for (const el of row.querySelectorAll('[data-fr-set-swatch]')) {
      const selected = el === swatch;
      el.setAttribute('aria-checked', String(selected));
      /** @type {HTMLElement} */ (el).tabIndex = selected ? 0 : -1;
    }
    const input = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-fr-set-check]'));
    if (input) {
      input.value = swatch.dataset.variantId || input.value;
      input.dataset.price = swatch.dataset.price || input.dataset.price;
      input.dataset.title = swatch.dataset.title || input.dataset.title;
      input.dataset.productId = swatch.dataset.productId || '';
    }
    const title = row.querySelector('[data-fr-set-title]');
    if (title && swatch.dataset.title) title.textContent = swatch.dataset.title;
    const sub = row.querySelector('[data-fr-set-sub]');
    if (sub && swatch.dataset.sub) sub.textContent = swatch.dataset.sub;
    const thumb = row.querySelector('[data-fr-set-thumb]');
    if (thumb && swatch.dataset.thumb) {
      let img = /** @type {HTMLImageElement | null} */ (thumb.querySelector('img'));
      if (!img) {
        img = document.createElement('img');
        img.className = 'fr-cts__thumb-img';
        img.alt = '';
        img.width = 48;
        img.height = 48;
        thumb.replaceChildren(img);
      }
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = swatch.dataset.thumb;
    }
    this.showError('');
    update(sectionOf(this));
  }
}

if (!customElements.get('fr-complete-the-set')) customElements.define('fr-complete-the-set', FrCompleteTheSet);

/* ------------------------------------------------------- multi add to cart */

/** @param {object[]} items @param {string[]} sectionIds */
async function postAdd(items, sectionIds) {
  /** @type {Record<string, unknown>} */
  const body = { items };
  if (sectionIds.length) {
    body.sections = sectionIds.join(',');
    body.sections_url = window.location.pathname;
  }
  const response = await fetch(`${ROOT}cart/add.js`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  /** @type {any} */
  let json = {};
  try {
    json = await response.json();
  } catch {}
  return { ok: response.ok && !json.status, status: response.status || json.status, body: json };
}

/** @param {Element} section @param {boolean} busy */
function setBusy(section, busy) {
  const targets = [addButtonOf(section), section.querySelector('[data-fr-sticky-button]')];
  for (const el of targets) {
    if (!el) continue;
    el.toggleAttribute('aria-busy', busy);
    el.classList.toggle('fr-is-loading', busy);
    /** @type {HTMLButtonElement} */ (el).disabled = busy;
  }
}

/**
 * Adds main item + checked add-ons in one request. Add-ons get properties._set = main product handle
 * (hidden at checkout, used by the cart to group lines under the main product).
 * @param {Element} section
 * @param {HTMLElement} productForm
 * @param {HTMLFormElement} form
 * @param {FrCompleteTheSet} set
 */
async function addSet(section, productForm, form, set) {
  const strings = stringsFor(section);
  const addons = set.selectedItems();
  const qty = quantityOf(section);
  const mainId = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value;
  if (!mainId) return;
  const handle = set.dataset.mainHandle || '';
  const items = [
    { id: Number(mainId), quantity: qty },
    ...addons.map((a) => ({ id: Number(a.id), quantity: 1, properties: { _set: handle } })),
  ];
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
    .filter((id) => !!id);

  set.showError('');
  setBusy(section, true);

  const events = await loadEvents();
  const CartEvent = events.CartLinesUpdateEvent;
  const deferred = CartEvent?.createPromise ? CartEvent.createPromise() : null;
  if (CartEvent && deferred) {
    productForm.dispatchEvent(
      new CartEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
  }

  /** @type {typeof items} */
  const failed = [];
  let mainFailed = false;
  let mainError = '';
  /** @type {any} */
  let sections = null;

  try {
    const result = await postAdd(items, sectionIds);
    if (result.ok) {
      sections = result.body.sections;
    } else if (result.status >= 400 && result.status < 500) {
      // 422 rejects the whole request: retry item by item so everything that can be added is added.
      const first = await postAdd([items[0]], sectionIds);
      if (!first.ok) {
        mainFailed = true;
        mainError = first.body?.description || first.body?.message || '';
      } else {
        sections = first.body.sections;
        for (const item of items.slice(1)) {
          const r = await postAdd([item], sectionIds);
          if (r.ok) sections = r.body.sections;
          else failed.push(item);
        }
      }
    } else {
      throw new Error(`cart/add.js ${result.status}`);
    }

    const cart = await fetch(`${ROOT}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
      (r) => r.json()
    );
    const added = items.filter((i) => !failed.includes(i)).reduce((s, i) => s + i.quantity, 0);
    deferred?.resolve({
      cart: CartEvent?.createCartFromAjaxResponse ? CartEvent.createCartFromAjaxResponse(cart) : cart,
      detail: {
        items: cart.items,
        source: 'fr-complete-the-set',
        sourceId: productForm.id || 'fr-complete-the-set',
        itemCount: mainFailed ? 0 : added,
        productId: productForm.dataset.productId,
        sections: sections || undefined,
        didError: mainFailed,
      },
    });
    if (!CartEvent) document.dispatchEvent(new CustomEvent('fr:cart-updated', { detail: { cart } }));

    if (mainFailed) {
      // Nothing was added; checked rows stay checked so the shopper can retry.
      set.showError(mainError || strings.error_main || '');
    } else {
      const failedIds = failed.map((f) => String(f.id));
      set.reset(addons.filter((a) => !failedIds.includes(a.id)).map((a) => a.id));
      if (failed.length) {
        const titles = addons
          .filter((a) => failedIds.includes(a.id))
          .map((a) => a.title)
          .join(', ');
        set.showError(fill(set.dataset.errorAddon || strings.error_addon || '', { title: titles }));
      } else {
        announce(strings.added || '');
      }
    }
  } catch (error) {
    deferred?.reject?.(error);
    set.showError(set.dataset.errorNetwork || strings.error_network || '');
  } finally {
    setBusy(section, false);
    update(section);
  }
}

// Capture on window runs before Horizon's document-level `on:submit` delegation, so the default single-item
// add never fires when add-ons are checked. Without checked add-ons Horizon's own submit runs untouched.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const productForm = /** @type {HTMLElement | null} */ (form.closest('product-form-component'));
    const section = sectionOf(productForm);
    if (!productForm || !section || !section.querySelector(MAIN_SELECTOR)) return;
    const set = setOf(section);
    if (!set || set.selectedItems().length === 0) return;
    const mainId = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="id"]'))?.value;
    if (set.dataset.mainVariantId && mainId && set.dataset.mainVariantId !== mainId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    addSet(section, productForm, form, set);
  },
  true
);

// Quantity changes (Horizon quantity selector + typing)
/** @param {Event} event */
function onQuantity(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target || !target.closest('product-form-component')) return;
  if (event.type !== 'quantity-selector:update' && !target.matches('input[name="quantity"]')) return;
  const section = sectionOf(target);
  if (section?.querySelector(MAIN_SELECTOR)) requestAnimationFrame(() => update(section));
}
document.addEventListener('quantity-selector:update', onQuantity);
document.addEventListener('input', onQuantity);
document.addEventListener('change', onQuantity);

/* ------------------------------------------------------- sticky bar */

class FrStickyBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;
  // Fifty Rooms (ads, 2026-10-26): the bar shows whenever the main button is off screen — above it after a scroll,
  // and also below the first screen on a phone, so a visitor from an ad sees a buy button right away.
  #buttonOff = false;
  #footerVisible = false;

  connectedCallback() {
    this.button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-fr-sticky-button]'));
    this.button?.addEventListener('click', this.#onClick);
    const section = sectionOf(this);
    const target = section ? addButtonOf(section) : null;
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (!('IntersectionObserver' in window) || !target) return;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === target) {
          this.#buttonOff = !entry.isIntersecting;
        } else {
          this.#footerVisible = entry.isIntersecting;
        }
      }
      this.#sync();
    });
    this.#observer.observe(target);
    if (footer) this.#observer.observe(footer);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.button?.removeEventListener('click', this.#onClick);
    document.documentElement.classList.remove('fr-sticky-bar-on');
  }

  /** @param {{label: string, total: string, count: string}} view */
  render({ label, total, count }) {
    const totalEl = this.querySelector('[data-fr-sticky-total]');
    if (totalEl) totalEl.textContent = total;
    const countEl = this.querySelector('[data-fr-sticky-count]');
    if (countEl) countEl.textContent = count;
    if (this.button && !this.button.disabled && !this.button.classList.contains('fr-is-loading')) {
      this.button.textContent = label;
    }
  }

  #sync() {
    const show = this.#buttonOff && !this.#footerVisible;
    this.dataset.visible = String(show);
    this.toggleAttribute('inert', !show);
    document.documentElement.classList.toggle('fr-sticky-bar-on', show);
  }

  #onClick = () => {
    const section = sectionOf(this);
    if (!section) return;
    const form = formOf(section);
    const submitter = addButtonOf(section);
    if (!form || !submitter || submitter.disabled) return;
    form.requestSubmit(submitter);
  };
}

if (!customElements.get('fr-sticky-bar')) customElements.define('fr-sticky-bar', FrStickyBar);

/* ------------------------------------------------------- size picker */

/** @param {Element | null} section */
function selectedSizeOf(section) {
  return /** @type {HTMLElement | null} */ (section?.querySelector('[data-fr-size][aria-checked="true"]') || null);
}

/**
 * Selects a size pill without a reload. Returns false when the page must navigate instead (no form / sold out).
 * @param {HTMLElement} pill
 * @param {{history?: boolean}} [options]
 */
export function selectSize(pill, { history: writeHistory = true } = {}) {
  const section = sectionOf(pill);
  const form = section ? formOf(section) : null;
  const idInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]') || null);
  const id = pill.dataset.variantId;
  if (!section || !idInput || !id || pill.dataset.available === 'false') return false;
  const current = selectedSizeOf(section);
  if (current && current.dataset.available === 'false') return false; // server rendered a sold-out state

  for (const el of pill.closest('[data-fr-size-picker]')?.querySelectorAll('[data-fr-size]') || []) {
    const selected = el === pill;
    el.setAttribute('aria-checked', String(selected));
    /** @type {HTMLElement} */ (el).tabIndex = selected ? 0 : -1;
  }
  const name = section.querySelector('[data-fr-size-name]');
  if (name) name.textContent = pill.dataset.title || '';

  idInput.value = id;
  idInput.dispatchEvent(new Event('change', { bubbles: true }));

  const price = Number(pill.dataset.price) || 0;
  const strings = stringsFor(section);
  const priceEl = /** @type {HTMLElement | null} */ (section.querySelector('[data-fr-main-price]'));
  if (priceEl) {
    priceEl.dataset.frMainPrice = String(price);
    const amount = priceEl.querySelector('.fr-pdp-heading__amount');
    if (amount) amount.textContent = formatMoney(price, priceEl.dataset.frCurrency, strings.money_format, strings.money_currency);
  }
  const set = setOf(section);
  if (set) {
    set.dataset.mainVariantId = id;
    set.dataset.mainPrice = String(price);
  }
  const stickyVariant = section.querySelector('[data-fr-sticky-variant]');
  if (stickyVariant) stickyVariant.textContent = pill.dataset.title || '';

  if (writeHistory && 'replaceState' in history) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    try {
      history.replaceState({ ...(history.state || {}), frPdp: true }, '', url);
    } catch {}
  }
  update(section);
  section.dispatchEvent(new CustomEvent('fr:size-change', { bubbles: true, detail: { variantId: id, price, title: pill.dataset.title } }));
  return true;
}

document.addEventListener('click', (event) => {
  const pill = event.target instanceof Element ? /** @type {HTMLElement | null} */ (event.target.closest('a[data-fr-size]')) : null;
  if (!pill) return;
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (pill.getAttribute('aria-checked') === 'true') {
    event.preventDefault();
    return;
  }
  if (selectSize(pill)) event.preventDefault();
});

// Arrow keys / Space inside the size radiogroup
document.addEventListener('keydown', (event) => {
  const pill = event.target instanceof Element ? /** @type {HTMLElement | null} */ (event.target.closest('a[data-fr-size]')) : null;
  if (!pill) return;
  const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
  const step = keys[/** @type {keyof typeof keys} */ (event.key)];
  if (!step && event.key !== ' ') return;
  event.preventDefault();
  const all = /** @type {HTMLElement[]} */ (Array.from(pill.closest('[data-fr-size-picker]')?.querySelectorAll('[data-fr-size]') || []));
  const next = step ? all[(all.indexOf(pill) + step + all.length) % all.length] : pill;
  if (!next) return;
  if (!selectSize(next)) {
    window.location.assign(/** @type {HTMLAnchorElement} */ (/** @type {unknown} */ (next)).href);
    return;
  }
  next.focus();
});

/* ------------------------------------------------------- design / type navigation */

/** @type {Map<string, {at: number, promise: Promise<Record<string, string | null> | null>}>} */
const sectionCache = new Map();
const CACHE_MS = 60_000;
let currentPath = window.location.pathname;
let navToken = 0;

/** @param {Element} anyInMain */
function sectionIdsFor(anyInMain) {
  const main = sectionOf(anyInMain);
  const mainId = main?.id.replace(/^shopify-section-/, '');
  if (!mainId) return null;
  const ids = [mainId];
  for (const el of document.querySelectorAll('[data-fr-pdp-refresh]')) {
    const id = /** @type {HTMLElement} */ (el).dataset.frPdpRefresh;
    if (id && !ids.includes(id) && document.getElementById(`shopify-section-${id}`)) ids.push(id);
  }
  return { mainId, ids: ids.slice(0, 5) }; // Section Rendering API: max 5 sections per request
}

/** @param {string} url @param {string[]} ids */
function fetchSections(url, ids) {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('sections', ids.join(','));
  const key = u.toString();
  const cached = sectionCache.get(key);
  if (cached && Date.now() - cached.at < CACHE_MS) return cached.promise;
  // No `Accept: application/json` here: on /products/<handle> Shopify then answers the product JSON
  // ({product: …}) instead of the Section Rendering payload.
  const promise = fetch(key, { credentials: 'same-origin' })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
  sectionCache.set(key, { at: Date.now(), promise });
  promise.then((data) => {
    if (!data) sectionCache.delete(key);
  });
  return promise;
}

/** @param {string} id @param {string} html */
function replaceSection(id, html) {
  const current = document.getElementById(`shopify-section-${id}`);
  if (!current || typeof html !== 'string') return;
  const template = document.createElement('template');
  template.innerHTML = html;
  const fresh = template.content.querySelector(`#shopify-section-${CSS.escape(id)}`);
  const nodes = Array.from((fresh || template.content).childNodes);
  current.replaceChildren(...nodes);
}

/**
 * @param {string} url
 * @param {{push: boolean, trigger?: HTMLElement | null}} options
 */
async function navigate(url, { push, trigger }) {
  const anchor = trigger || document.querySelector(`${MAIN_SELECTOR}`);
  const meta = anchor ? sectionIdsFor(anchor) : null;
  if (!meta) {
    window.location.assign(url);
    return;
  }
  const keepSize = selectedSizeOf(sectionOf(anchor))?.dataset.title || '';
  const token = ++navToken;
  const kind = trigger?.hasAttribute('data-fr-type-card') ? 'type' : 'swatch';
  const main = document.getElementById(`shopify-section-${meta.mainId}`);
  main?.setAttribute('aria-busy', 'true');

  const data = await fetchSections(url, meta.ids);
  if (token !== navToken) return; // a newer navigation won
  main?.removeAttribute('aria-busy');
  if (!data || typeof data[meta.mainId] !== 'string' || !data[meta.mainId].includes('data-fr-pdp-main')) {
    window.location.assign(url);
    return;
  }

  for (const id of meta.ids) replaceSection(id, /** @type {string} */ (data[id]));

  const newMain = document.getElementById(`shopify-section-${meta.mainId}`);
  const metaEl = /** @type {HTMLElement | null} */ (newMain?.querySelector(MAIN_SELECTOR) || null);
  const title = metaEl?.dataset.pageTitle || document.title;
  const canonical = metaEl?.dataset.canonical || url;

  if (push) history.pushState({ frPdp: true }, '', url);
  currentPath = new URL(url, window.location.origin).pathname;
  document.title = title;
  document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
  document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonical);
  document.querySelector('meta[property="og:title"]')?.setAttribute('content', metaEl?.dataset.productTitle || title);

  if (newMain) {
    // same size on the next design (when it has one and the URL did not ask for a variant itself)
    if (keepSize && !new URL(url, window.location.origin).searchParams.has('variant')) {
      const same = /** @type {HTMLElement | undefined} */ (
        Array.from(newMain.querySelectorAll('[data-fr-size]')).find((el) => /** @type {HTMLElement} */ (el).dataset.title === keepSize)
      );
      if (same && same.getAttribute('aria-checked') !== 'true') selectSize(same, { history: push });
    }
    update(newMain);
    // keep keyboard focus on the equivalent control of the new product
    if (trigger && (document.activeElement === document.body || !document.activeElement?.isConnected)) {
      const selector = kind === 'type' ? '[data-fr-type-card][aria-current="page"]' : '[data-fr-swatch][aria-current="page"]';
      /** @type {HTMLElement | null} */ (newMain.querySelector(selector))?.focus({ preventScroll: true });
    }
  }
  const strings = stringsFor(newMain);
  announce(fill(strings.now_showing || '[title]', { title: metaEl?.dataset.productTitle || title }));

  // Analytics: the swapped <product-component view-event-payload> emits Horizon's product view event on
  // intersect; additionally publish the standard page view for the new URL.
  const events = await loadEvents();
  if (events.PageViewEvent) {
    document.dispatchEvent(new events.PageViewEvent({ page: { template: 'product', title, url: window.location.href } }));
  }
  document.dispatchEvent(new CustomEvent('fr:pdp-navigated', { detail: { url: window.location.href, title } }));
}

/** @param {MouseEvent} event */
function onNavClick(event) {
  const link = event.target instanceof Element ? /** @type {HTMLAnchorElement | null} */ (event.target.closest('a[data-fr-nav-link]')) : null;
  if (!link) return;
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (window.Shopify?.designMode) return;
  if (link.getAttribute('aria-current') === 'page') {
    event.preventDefault();
    return;
  }
  if (link.dataset.frNav === 'reload' || link.origin !== window.location.origin || !('pushState' in history)) return;
  event.preventDefault();
  navigate(link.href, { push: true, trigger: link });
}

/** @param {Event} event */
function onNavIntent(event) {
  const link = event.target instanceof Element ? /** @type {HTMLAnchorElement | null} */ (event.target.closest('a[data-fr-nav-link]')) : null;
  if (!link || link.dataset.frNav === 'reload' || link.getAttribute('aria-current') === 'page') return;
  if (window.Shopify?.designMode || link.origin !== window.location.origin) return;
  const meta = sectionIdsFor(link);
  if (meta) fetchSections(link.href, meta.ids);
}

document.addEventListener('click', onNavClick);
document.addEventListener('pointerover', onNavIntent, { passive: true });
document.addEventListener('focusin', onNavIntent);

window.addEventListener('popstate', (event) => {
  if (!event.state || !event.state.frPdp) return;
  if (window.location.pathname === currentPath) return; // e.g. a drawer closing via history
  navigate(window.location.href, { push: false, trigger: null });
});

/* ------------------------------------------------------- init */

function init() {
  const mains = document.querySelectorAll(MAIN_SELECTOR);
  if (!mains.length) return;
  if (!history.state || !history.state.frPdp) {
    try {
      history.replaceState({ ...(history.state || {}), frPdp: true }, '');
    } catch {}
  }
  for (const el of mains) update(sectionOf(el));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
else init();
